import React, { useState } from 'react';
import { FileText, Download, Copy, Eye, Code2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ProcessedFileResponse } from '@/types/api';
import { useToast } from '@/hooks/use-toast';

interface MarkdownViewerProps {
  fileData: ProcessedFileResponse;
}

type ViewMode = 'preview' | 'raw';

export function MarkdownViewer({ fileData }: MarkdownViewerProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('preview');
  const { toast } = useToast();

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(fileData.content);
      toast({
        title: "Copied to clipboard",
        description: "Markdown content has been copied to clipboard.",
      });
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Could not copy content to clipboard.",
        variant: "destructive",
      });
    }
  };

  const downloadFile = () => {
    try {
      const blob = new Blob([fileData.content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileData.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Download started",
        description: `Downloading ${fileData.filename}...`,
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Could not download the processed file.",
        variant: "destructive",
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg truncate">{fileData.filename}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant={viewMode === 'raw' ? 'default' : 'outline'} 
              size="sm" 
              onClick={() => setViewMode(prev => prev === 'preview' ? 'raw' : 'preview')}
            >
              {viewMode === 'preview' ? (
                <>
                  <Code2 className="w-4 h-4 mr-2" />
                  Raw
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </>
              )}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={copyToClipboard}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={downloadFile}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
        
        {/* File Info */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>{formatFileSize(fileData.size_bytes)}</span>
          <span>{fileData.lines} lines</span>
          <span>Processed: {formatDate(fileData.modified)}</span>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col min-h-0 p-0">
        {/* Markdown Content */}
        <div className="flex-1 bg-gray-50 rounded-md overflow-auto">
          <div className="p-4">
            {viewMode === 'preview' ? (
              /* Rendered Markdown Preview */
              <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:font-semibold prose-p:text-gray-700 prose-strong:text-gray-900">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 text-gray-900">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-xl font-semibold mb-3 text-gray-800">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-lg font-semibold mb-2 text-gray-800">{children}</h3>,
                    p: ({ children }) => <p className="mb-4 text-gray-700 leading-relaxed">{children}</p>,
                    strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                    code: ({ inline, children }) => 
                      inline ? (
                        <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono text-gray-800">
                          {children}
                        </code>
                      ) : (
                        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                          <code>{children}</code>
                        </pre>
                      ),
                    ul: ({ children }) => <ul className="list-disc list-inside mb-4 space-y-2">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal list-inside mb-4 space-y-2">{children}</ol>,
                    li: ({ children }) => <li className="text-gray-700">{children}</li>,
                  }}
                >
                  {fileData.content}
                </ReactMarkdown>
              </div>
            ) : (
              /* Raw Markdown Source */
              <div className="prose prose-sm max-w-none">
                <div className="font-mono text-sm whitespace-pre-wrap">
                  {fileData.content}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}